/**
 * 修饰项测试沙盒：纯前端估算，移植自旧版 www/legacy/admin.html 的 testModifiers()。
 * 只用于帮管理员核对附加费用配置，不调用后端、不代表最终报价（以后端 rule_engine 为准）。
 */
import type { Modifier } from "../api";

export interface SandboxInput {
  qty: number;           // 数量（张/个）
  edgeLen: number;       // 包边长度（米）
  baseUnitPrice: number; // 基础单价（元/张或㎡）
  doubleSided: boolean;
  cutting: boolean;
  smallBatch: boolean;   // 小批量场景
}

export interface SandboxLine {
  text: string;
  /** 加粗显示的金额 */
  value?: string;
  /** 小批量特价是否生效 */
  status?: "on" | "off";
}

export interface SandboxResult {
  lines: SandboxLine[];
  warnings: string[];
  total: number;
  unitPrice: number;
}

function parseMods(mods: Modifier[]): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const m of mods) {
    if (!m.modifier_type || m.is_active === 0) continue; // 停用的修饰项不参与计价 (同后端规则引擎)
    if (typeof m.params_json !== "string") { out[m.modifier_type] = m.params_json ?? {}; continue; }
    try { out[m.modifier_type] = JSON.parse(m.params_json || "{}") as Record<string, unknown>; }
    catch { out[m.modifier_type] = {}; }
  }
  return out;
}

const f = (v: unknown) => parseFloat(String(v));

export function runModifierSandbox(modifiers: Modifier[], input: SandboxInput): SandboxResult {
  const qty = Math.trunc(input.qty) || 1;
  const edgeLen = input.edgeLen || 0;
  const baseUnitPrice = input.baseUnitPrice || 0;
  const mods = parseMods(modifiers);

  const lines: SandboxLine[] = [];
  const warnings: string[] = [];
  let basePrice = baseUnitPrice * qty;
  lines.push({ text: `基础价：${baseUnitPrice} × ${qty} =`, value: `${basePrice.toFixed(2)} 元` });

  // double_sided_multiplier
  if (input.doubleSided) {
    const dsMod = mods["double_sided_multiplier"];
    if (dsMod) {
      const factor = f(dsMod.factor) || 1.6;
      const after = basePrice * factor;
      lines.push({ text: `双面倍数：${basePrice.toFixed(2)} × ${factor} =`, value: `${after.toFixed(2)} 元` });
      basePrice = after;
    } else {
      warnings.push("勾选了双面，但未配置双面倍数（将使用默认系数 1.6）");
      const after = basePrice * 1.6;
      lines.push({ text: `双面倍数（默认）：${basePrice.toFixed(2)} × 1.6 =`, value: `${after.toFixed(2)} 元` });
      basePrice = after;
    }
  }

  // edge_fee
  let edgeFee = 0;
  if (edgeLen > 0) {
    const edgeMod = mods["edge_fee"];
    if (edgeMod) {
      const pm = f(edgeMod.per_meter) || 0;
      edgeFee = edgeLen * pm * qty;
      lines.push({ text: `包边费：${edgeLen}m × ${pm}元/m × ${qty} =`, value: `${edgeFee.toFixed(2)} 元` });
    } else {
      warnings.push("填写了包边长度，但未配置包边费，包边费 = 0");
    }
  }

  // cutting_fee
  let cuttingFee = 0;
  if (input.cutting) {
    const cutMod = mods["cutting_fee"];
    if (cutMod) {
      const ps = f(cutMod.per_sheet) || 0.5;
      cuttingFee = ps * qty;
      lines.push({ text: `裁切费：${ps}元/张 × ${qty} =`, value: `${cuttingFee.toFixed(2)} 元` });
    } else {
      warnings.push("勾选了裁切，但未配置裁切费（将使用默认 0.5 元/张）");
      cuttingFee = 0.5 * qty;
      lines.push({ text: `裁切费（默认）：0.5元/张 × ${qty} =`, value: `${cuttingFee.toFixed(2)} 元` });
    }
  }

  // small_batch_pricing（仅展示，不影响主计算）
  const sb = mods["small_batch_pricing"];
  if (input.smallBatch && sb && qty > 1) {
    const firstN = parseInt(String(sb.first_n_at_tier)) || 1;
    const subYuan = f(sb.subsequent_yuan) || 1.0;
    const normalTotal = baseUnitPrice * qty;
    const rest = Math.max(0, qty - firstN);
    const specialTotal = baseUnitPrice * Math.min(firstN, qty) + rest * subYuan;
    if (specialTotal < normalTotal) {
      lines.push({
        text: `小批量特价：前${firstN}张 × ${baseUnitPrice}元 + 后${rest}张 × ${subYuan}元 =`,
        value: `${specialTotal.toFixed(2)} 元`,
        status: "on",
      });
      lines.push({ text: `比正常价 ${normalTotal.toFixed(2)} 元节省 ${(normalTotal - specialTotal).toFixed(2)} 元` });
    } else {
      lines.push({
        text: `小批量特价：特价 ${specialTotal.toFixed(2)} 元 ≥ 正常价 ${normalTotal.toFixed(2)} 元`,
        status: "off",
      });
    }
  }

  let total = basePrice + edgeFee + cuttingFee;

  // min_price：旧版沙盒只提示「仅厚度×尺寸策略生效」，但现在的 rule_engine 对面积产品
  // (compute_single) 和尺寸产品都会把低于 floor 的总价补到 floor，这里按后端实际行为计算。
  if (mods["min_price"]) {
    const floor = f(mods["min_price"].floor) || 0;
    if (floor > 0 && total < floor) {
      lines.push({ text: `最低起价：${total.toFixed(2)} 元低于 ${floor} 元，按最低起价收 =`, value: `${floor.toFixed(2)} 元` });
      total = floor;
    } else {
      lines.push({ text: `最低起价：${floor} 元（未触发）` });
    }
  }

  return { lines, warnings, total, unitPrice: total / qty };
}
